import React, { useState } from "react";
import {
  Dialog,
  Stack,
  Box,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  IconButton,
  TextField,
  Text,
  Button,
  Divider,
  Hidden,
} from "@origin-digital/ods-core";
import { IconMinus, IconPlus } from "@origin-digital/ods-icons";
import { isEmptyString, populateTotalLoadSummary } from "../helper";
import { GasOptionEnum } from "../types";

export interface Appliance {
  name: string;
  quantity: number;
  load: number;
  unitLoad: number;
  loadStep: number;
}

export const initialAppliances: Appliance[] = [
  { name: "Wall oven", quantity: 0, load: 0, unitLoad: 50, loadStep: 5 },
  { name: "Upright stove", quantity: 0, load: 0, unitLoad: 65, loadStep: 5 },
  { name: "Hot plate", quantity: 0, load: 0, unitLoad: 40, loadStep: 5 },
  { name: "BBQ", quantity: 0, load: 0, unitLoad: 60, loadStep: 5 },
  { name: "Space heater", quantity: 0, load: 0, unitLoad: 35, loadStep: 5 },
  {
    name: "Ducted heating",
    quantity: 0,
    load: 0,
    unitLoad: 100,
    loadStep: 5,
  },
  { name: "Fireplace", quantity: 0, load: 0, unitLoad: 35, loadStep: 5 },
  {
    name: "Hot water, cont. flow",
    quantity: 0,
    load: 0,
    unitLoad: 200,
    loadStep: 5,
  },
  {
    name: "Hot water, storage",
    quantity: 0,
    load: 0,
    unitLoad: 40,
    loadStep: 5,
  },
  {
    name: "Hot water, instant",
    quantity: 0,
    load: 0,
    unitLoad: 180,
    loadStep: 5,
  },
  { name: "Pool heater", quantity: 0, load: 0, unitLoad: 300, loadStep: 5 },
  { name: "SPA heater", quantity: 0, load: 0, unitLoad: 60, loadStep: 5 },
];

interface GasAppliancesDialogProps {
  closeDialog: () => void;
  setFieldValue: (field: string, value: any) => void;
  appliancesFromFormik: Appliance[];
  totalLoadFromFormik?: string;
  gasOptionName: GasOptionEnum;
}

const GasAppliancesDialog: React.FC<GasAppliancesDialogProps> = ({
  closeDialog,
  setFieldValue,
  appliancesFromFormik,
  totalLoadFromFormik,
  gasOptionName,
}) => {
  const [appliances, setAppliances] = useState(appliancesFromFormik);
  const [totalLoad, setTotalLoad] = useState(
    !totalLoadFromFormik || isEmptyString(totalLoadFromFormik)
      ? 0
      : parseInt(totalLoadFromFormik, 10)
  );
  const [error, setError] = useState<string | null>(null);

  const handleQuantityChange = (index: number, change: number) => {
    const updatedAppliances = appliances.map((appliance, i) => {
      if (i === index) {
        // If quantity is 0 and trying to increase, set load to unit load
        if (appliance.quantity === 0 && change === 1) {
          return { ...appliance, load: appliance.unitLoad, quantity: 1 };
        }
        // If quantity is 1 and trying to decrease, set load to 0
        if (appliance.quantity === 1 && change === -1) {
          return { ...appliance, load: 0, quantity: 0 };
        }
        const newQuantity = Math.max(appliance.quantity + change, 0);
        return { ...appliance, quantity: newQuantity };
      }
      return appliance;
    });
    setAppliances(updatedAppliances);
    calculateTotalLoad(updatedAppliances);
  };

  const handleLoadChange = (index: number, change: number) => {
    const updatedAppliances = appliances.map((appliance, i) => {
      if (i === index) {
        const newLoad = Math.max(appliance.load + change, 0);
        return { ...appliance, load: newLoad };
      }
      return appliance;
    });
    setAppliances(updatedAppliances);
    calculateTotalLoad(updatedAppliances);
  };

  const calculateTotalLoad = (_appliances: Appliance[]) => {
    const total = _appliances.reduce(
      (acc, _appliance) => acc + _appliance.quantity * _appliance.load,
      0
    );
    setTotalLoad(total);
  };

  const totalLoadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "") {
      setTotalLoad(
        appliances.reduce(
          (acc, appliance) => acc + appliance.quantity * appliance.load,
          0
        )
      );
      return;
    }
    if (isNaN(parseInt(event.target.value, 10))) {
      return;
    }
    const newTotalLoad = parseInt(event.target.value, 10);
    setTotalLoad(newTotalLoad);
  };

  const handleSubmit = () => {
    if (appliances.every((appliance) => appliance.quantity === 0)) {
      setError(
        "Please enter at least one appliance with quantity greater than 0"
      );
      return;
    }

    setFieldValue(
      `${gasOptionName}.totalLoadSummary`,
      populateTotalLoadSummary(appliances)
    );
    setFieldValue(`${gasOptionName}.totalLoad`, totalLoad.toString());
    setFieldValue(`${gasOptionName}.totalLoadDetails`, [...appliances]);
    closeDialog();
  };

  const handleReset = () => {
    setAppliances(initialAppliances);
    setTotalLoad(0);
    setError(null);
  };

  return (
    <Dialog
      id="dialog"
      open={true}
      title="Gas appliances"
      onClose={closeDialog}
    >
      <Stack space="small">
        <Box paddingBottom={{ xs: "xlarge", md: "none" }}>
          <Table
            aria-label="Load calculator"
            textVariant="body-small"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell>Appliance</TableCell>
                <TableCell alignX="center">Quantity</TableCell>
                <TableCell alignX="center">Load (MJ/h)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appliances.map((appliance, index) => (
                <TableRow key={appliance.name}>
                  <TableCell
                    alignY="center"
                    // @ts-expect-error: we need to customise the style
                    style={{
                      paddingRight: 0,
                    }}
                  >
                    {appliance.name}
                  </TableCell>
                  <TableCell fitContent={true} alignY="center">
                    <Box display="flex">
                      <IconButton
                        aria-label={`Decrease ${appliance.name} quantity by 1`}
                        noIconPadding
                        onClick={() => handleQuantityChange(index, -1)}
                        disabled={appliance.quantity === 0}
                        color="primary"
                      >
                        <IconMinus size="xsmall" />
                      </IconButton>
                      <Box marginX="xsmall" style={{ width: 16 }}>
                        <Text
                          align="center"
                          // @ts-expect-error: incorrect ods tone type
                          tone={
                            appliance.quantity === 0
                              ? "disabled"
                              : "neutralDark"
                          }
                          weight="medium"
                        >
                          {appliance.quantity}
                        </Text>
                      </Box>
                      <IconButton
                        aria-label={`Increase ${appliance.name} quantity by 1`}
                        noIconPadding
                        onClick={() => handleQuantityChange(index, 1)}
                        color="primary"
                      >
                        <IconPlus size="xsmall" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell fitContent={true} alignY="center">
                    <Box
                      display="flex"
                      style={
                        appliance.quantity === 0 ? { visibility: "hidden" } : {}
                      }
                    >
                      <IconButton
                        aria-label={`Decrease ${appliance.name} load`}
                        noIconPadding
                        onClick={() =>
                          handleLoadChange(index, -appliance.loadStep)
                        }
                        disabled={appliance.load === appliance.loadStep}
                        color="primary"
                      >
                        <IconMinus size="xsmall" />
                      </IconButton>
                      <Box marginX="xsmall" style={{ width: 24 }}>
                        <Text align="center" tone="neutralDark" weight="medium">
                          {appliance.load}
                        </Text>
                      </Box>
                      <IconButton
                        aria-label={`Increase ${appliance.name} load`}
                        noIconPadding
                        onClick={() =>
                          handleLoadChange(index, appliance.loadStep)
                        }
                        color="primary"
                      >
                        <IconPlus size="xsmall" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box
          display="inline-flex"
          justifyContent="space-between"
          alignItems="center"
          width="full"
          paddingTop={{ lg: "medium", xs: "medium" }}
          paddingBottom={{ lg: "medium", md: "xxxlarge", xs: "large" }}
        >
          <Text tone="neutralDark" weight="medium">
            Total load (MJ/h)
          </Text>
          <TextField
            aria-label="Total load (MJ/h)"
            variant="outlined"
            size="small"
            hideHelperTextSpace={true}
            // @ts-expect-error: we need customise style
            style={{ width: 88 }}
            domProps={{
              style: { textAlign: "center" },
            }}
            value={totalLoad}
            onChange={totalLoadChange}
          />
        </Box>
        {error && (
          <Box
            width="full"
            paddingTop={{ lg: "medium", xs: "medium" }}
            paddingBottom={{ lg: "medium", md: "xxxlarge", xs: "large" }}
          >
            <Text tone="critical" weight="medium">
              {error}
            </Text>
          </Box>
        )}

        {/* Action container */}
        <Box
          position={{ lg: "static", xs: "fixed" }}
          backgroundColor="white"
          width="full"
          style={{ left: "0", bottom: "0", zIndex: "1" }}
        >
          <Hidden above="lg">
            <Divider />
          </Hidden>
          <Box
            display="flex"
            flexWrap={{ lg: "nowrap", xs: "wrap" }}
            justifyContent="space-between"
            alignItems="center"
            paddingTop={{ lg: "medium", xs: "medium" }}
            paddingBottom={{ lg: "none", xs: "medium" }}
            paddingX={{ lg: "none", xs: "xlarge" }}
          >
            <Button
              variant="text"
              color="primary"
              noTextPadding
              onClick={handleReset}
            >
              Reset
            </Button>
            <Box display="flex">
              <Box marginRight="medium">
                <Button variant="text" color="primary" onClick={closeDialog}>
                  Cancel
                </Button>
              </Box>
              <Button onClick={handleSubmit}>Apply</Button>
            </Box>
          </Box>
        </Box>
      </Stack>
    </Dialog>
  );
};

export default GasAppliancesDialog;
